// 「分頁開很久之後回來，一換頁就 😵」的根治 (2026-08-03)。
//
// 症狀的兩個面向其實是**同一個根因**：分頁還在跑舊的 build。
//   (a) 舊 bundle 去抓已經不存在的 lazy chunk → 404 → ChunkLoadError
//       （chunk-reload.ts 事後補救：重整一次。 但那是「撞到才處理」。）
//   (b) 舊 bundle × 新後端 → API 回傳的形狀跟舊前端的假設對不上 → render 期間
//       TypeError → 這**不是** chunk 錯誤 → 直接落到 ErrorBoundary 的 😵 畫面。
//       (b) 完全躲過 chunk-reload 的偵測，而我們一天可能部署好幾次。
//
// 這支把它從「撞到才處理」變成「先知道」：定期比對伺服器上的 index.html 用的
// entry script 檔名（Vite 會 hash，換 build 就換名字）。 發現不一樣 = 伺服器已經
// 換版 → 標記起來，下一次換頁時用**整頁導航**取代前端路由，使用者就直接落在新版
// 上，兩種錯誤都不會發生。
//
// 為什麼挑「換頁」這個時機：那本來就是要離開目前畫面的動作，不會吃掉打到一半的
// 表單；而在原地強制重整會。 所以這裡刻意**不**主動打斷使用者。

/**
 * 目前這個分頁正在跑的 entry script（Vite 產生的 hash 檔名）。
 *
 * ⚠ 只認 /assets/ 底下的 —— dev server 的 DOM 裡有兩個 module script
 * （`/@vite/client` 跟 `/src/main.tsx`），而 index.html 原始檔只寫了後者。
 * 直接拿 querySelector 的第一個會抓到 `/@vite/client`，跟 index.html 對不起來
 * → 每次本機開發都會被判定成「伺服器換版了」→ 每次換頁都整頁重載。
 * 正式 build 只有一個 `/assets/index-<hash>.js`；抓不到就代表不是正式 build，
 * 這個機制自動停用（回傳 null）。
 */
function currentEntrySrc(): string | null {
  for (const el of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const path = new URL(el.src, location.origin).pathname;
    if (path.startsWith('/assets/')) return path;
  }
  return null;
}

let known: string | null = null;
let stale = false;
const listeners = new Set<() => void>();

function markStale() {
  if (stale) return;
  stale = true;
  for (const fn of listeners) fn();
}

/** 伺服器上的 build 是否已經跟這個分頁不同。 */
export function isBuildStale(): boolean {
  return stale;
}

export function subscribeBuildStale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * 跟伺服器對一次版本。 只讀 index.html（幾 KB、gzip 過），不新增任何後端端點。
 * 任何失敗都當作「沒事」—— 版本檢查絕不可以自己變成故障來源。
 */
export async function checkForNewBuild(): Promise<void> {
  if (stale) return;
  known ??= currentEntrySrc();
  if (!known) return; // dev server 沒有 hash 檔名 → 不做這件事
  try {
    const res = await fetch('/index.html', { cache: 'no-store' });
    if (!res.ok) return;
    const html = await res.text();
    const m = html.match(/<script[^>]+type="module"[^>]+src="([^"]+)"/);
    const next = m?.[1];
    // 同樣只比 /assets/ 的 entry —— 對不上格式就當作沒事，寧可不作為也不要誤判。
    if (next?.startsWith('/assets/') && next !== known) markStale();
  } catch {
    // 離線 / 網路抖動 —— 不是版本問題，安靜跳過。
  }
}

/**
 * 換頁時如果已知伺服器換版了，用整頁導航取代前端路由。
 * 回傳 true = 已經接手導航，呼叫端不要再做別的事。
 *
 * 節流跟 chunk-reload 共用同一把鎖：萬一新版本身就會壞，不會變成無限重整。
 */
export function hardNavigateIfStale(to: string): boolean {
  if (!stale) return false;
  const KEY = 'ts-build-reload-at';
  try {
    const last = Number(sessionStorage.getItem(KEY) || '0');
    if (Date.now() - last < 10_000) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // sessionStorage 不可用 → 還是導航，只是少了防連環重整的保險。
  }
  window.location.assign(to);
  return true;
}

/** 開機掛上：回到分頁時 + 每 5 分鐘 對一次版本。 回傳解除函式。 */
export function startBuildVersionWatch(): () => void {
  known ??= currentEntrySrc();
  const onVisible = () => {
    if (document.visibilityState === 'visible') void checkForNewBuild();
  };
  // focus 不再多驗一次 visibility —— 視窗拿到焦點本來就代表使用者在看它，
  // 而多那一層判斷會讓這條路徑在自動化環境（分頁永遠是 hidden）完全跑不到。
  const onFocus = () => void checkForNewBuild();
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onFocus);
  const timer = window.setInterval(() => void checkForNewBuild(), 5 * 60_000);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onFocus);
    window.clearInterval(timer);
  };
}
